import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { RenewalPaymentStatusPanel } from "../components/RenewalPaymentStatusPanel";
import { UnpublishConfirmPanel } from "../components/UnpublishConfirmPanel";
import {
  IconEye,
  IconPencil,
  IconRefresh,
  IconTrash,
} from "../components/BenchmarkIcons";
import { StarRating } from "../components/StarRating";
import {
  fetchPublisherDashboard,
  formatApplicationStatusLabel,
  resolvePublisherDashboardSelection,
  SELECTED_APP_DOWNLOADS_LABEL,
  type PublisherDashboardApp,
  type PublisherDashboardData,
  type PublisherDashboardLocationState,
} from "../services/publisherDashboard";
import { useAuth } from "../context/AuthContext";
import {
  canOpenUnpublishConfirmation,
  resolveUnpublishErrorMessage,
  resolveUnpublishSuccessMessage,
  unpublishApplication,
} from "../services/publisherUnpublish";
import { openRazorpayRenewalCheckout } from "../services/razorpayCheckout";
import {
  createRenewalIntent,
  fetchPublisherRenewalStatus,
  fetchRenewalProviderAvailability,
  fetchRenewalQuote,
  formatInrPaise,
  verifyRazorpayRenewalPayment,
  type PublisherRenewalTransactionStatus,
  type RenewalProviderAvailabilityMap,
  type RenewalQuote,
} from "../services/renewal";
import {
  isTerminalRenewalStatus,
  pollRenewalStatus,
} from "../services/renewalPaymentPolling";
import type { PaymentProvider } from "../services/payments";

const GRID_COLUMNS = 4;
const BENCHMARK_SCROLL_SLOTS = GRID_COLUMNS * 5;
const METRIC_LOADING_PLACEHOLDER = "\u00a0";

let cachedDashboardData: PublisherDashboardData | null = null;
let cachedSelectedApplicationId: string | null = null;

function readDashboardLocationState(
  state: unknown,
): PublisherDashboardLocationState | null {
  if (!state || typeof state !== "object") {
    return null;
  }

  return state as PublisherDashboardLocationState;
}

function resolveCachedSelectedApplicationId(
  dashboard: PublisherDashboardData | null,
  requestedApplicationId?: string | null,
): string | null {
  return resolvePublisherDashboardSelection(dashboard?.apps ?? [], {
    requestedApplicationId,
    currentApplicationId: cachedSelectedApplicationId,
  });
}

function formatDownloads(count: number): string {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)} M`;
  }

  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)} K`;
  }

  return String(count);
}

export function PublisherDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialNavigationState = readDashboardLocationState(location.state);
  const [data, setData] = useState<PublisherDashboardData | null>(cachedDashboardData);
  const [message, setMessage] = useState(
    () => initialNavigationState?.publishMessage ?? "",
  );
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(
    () => {
      const initialSelection = resolveCachedSelectedApplicationId(
        cachedDashboardData,
        initialNavigationState?.selectedApplicationId,
      );
      cachedSelectedApplicationId = initialSelection;
      return initialSelection;
    },
  );
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [renewQuote, setRenewQuote] = useState<RenewalQuote | null>(null);
  const [renewProviders, setRenewProviders] =
    useState<RenewalProviderAvailabilityMap | null>(null);
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewTransactionId, setRenewTransactionId] = useState<string | null>(
    null,
  );
  const [renewStatus, setRenewStatus] =
    useState<PublisherRenewalTransactionStatus | null>(null);
  const [renewStatusPolling, setRenewStatusPolling] = useState(false);
  const [renewProviderLoading, setRenewProviderLoading] = useState<
    PaymentProvider | null
  >(null);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [unpublishLoading, setUnpublishLoading] = useState(false);
  const pollControllerRef = useRef(0);
  const unpublishRequestRef = useRef(0);

  const loadDashboard = useCallback(() => {
    return fetchPublisherDashboard()
      .then((dashboard) => {
        cachedDashboardData = dashboard;
        setData(dashboard);
        const navigationState = readDashboardLocationState(location.state);
        setSelectedApplicationId((current) => {
          const next = resolvePublisherDashboardSelection(dashboard.apps, {
            requestedApplicationId: navigationState?.selectedApplicationId,
            currentApplicationId: current,
          });
          cachedSelectedApplicationId = next;
          return next;
        });
        if (navigationState?.publishMessage) {
          setMessage(navigationState.publishMessage);
        }
      })
      .catch(() => {
        cachedDashboardData = null;
        cachedSelectedApplicationId = null;
        setData(null);
        setSelectedApplicationId(null);
      });
  }, [location.state]);

  const refreshRenewalStatus = useCallback(
    async (transactionId: string, poll = false) => {
      const pollToken = pollControllerRef.current + 1;
      pollControllerRef.current = pollToken;
      setRenewStatusPolling(poll);

      try {
        if (poll) {
          const finalStatus = await pollRenewalStatus({
            fetchStatus: () => fetchPublisherRenewalStatus(transactionId),
            getLifecycleStatus: (status) => status.status,
            onUpdate: (status) => {
              if (pollControllerRef.current === pollToken) {
                setRenewStatus(status);
              }
            },
            onTerminal: (status) => {
              if (status.renewalCompleted) {
                void loadDashboard();
              }
            },
          });

          if (pollControllerRef.current === pollToken) {
            setRenewStatus(finalStatus);
            if (finalStatus.renewalCompleted) {
              setMessage("Renewal complete.");
            } else if (!isTerminalRenewalStatus(finalStatus.status)) {
              setMessage(
                "Payment received or pending. Verification may take a moment.",
              );
            } else {
              setMessage(finalStatus.message);
            }
          }
        } else {
          const status = await fetchPublisherRenewalStatus(transactionId);
          if (pollControllerRef.current === pollToken) {
            setRenewStatus(status);
            setMessage(status.message);
            if (status.renewalCompleted) {
              void loadDashboard();
            }
          }
        }
      } catch {
        if (pollControllerRef.current === pollToken) {
          setMessage(
            "Payment received or pending. Verification may take a moment.",
          );
        }
      } finally {
        if (pollControllerRef.current === pollToken) {
          setRenewStatusPolling(false);
        }
      }
    },
    [loadDashboard],
  );

  useEffect(() => {
    const renewalState = searchParams.get("renewal");
    const paymentId = searchParams.get("paymentId");

    if (!renewalState || !paymentId) {
      return;
    }

    setRenewTransactionId(paymentId);
    setSearchParams({});

    if (renewalState === "cancelled") {
      setMessage("Renewal checkout was cancelled. Your app validity is unchanged.");
      return;
    }

    if (renewalState === "success" || renewalState === "pending") {
      setMessage("Payment received. Verification in progress.");
      void refreshRenewalStatus(paymentId, true);
    }
  }, [refreshRenewalStatus, searchParams, setSearchParams]);

  useEffect(() => {
    const navigationState = readDashboardLocationState(location.state);
    if (!navigationState?.selectedApplicationId && !navigationState?.publishMessage) {
      return;
    }

    if (navigationState.selectedApplicationId) {
      setSelectedApplicationId((current) => {
        const next = resolvePublisherDashboardSelection(data?.apps ?? [], {
          requestedApplicationId: navigationState.selectedApplicationId,
          currentApplicationId: current,
        });
        cachedSelectedApplicationId = next;
        return next;
      });
    }

    if (navigationState.publishMessage) {
      setMessage(navigationState.publishMessage);
    }
  }, [data?.apps, location.key, location.state]);

  useEffect(() => {
    if (!user) {
      return;
    }

    void loadDashboard();
  }, [loadDashboard, user]);

  useEffect(() => {
    if (!user) {
      pollControllerRef.current += 1;
      unpublishRequestRef.current += 1;
      cachedDashboardData = null;
      cachedSelectedApplicationId = null;
      setData(null);
      setSelectedApplicationId(null);
      setMessage("");
      setRenewQuote(null);
      setRenewProviders(null);
      setRenewLoading(false);
      setRenewTransactionId(null);
      setRenewStatus(null);
      setRenewStatusPolling(false);
      setRenewProviderLoading(null);
      setShowUnpublishConfirm(false);
      setUnpublishLoading(false);
    }
  }, [user]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTick(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const apps = data?.apps ?? [];
  const metricsReady = data !== null;
  const publisherName = data?.publisher.displayName ?? "";
  const totalApps = data?.publisher.totalApps ?? 0;
  const membershipStatus = data?.publisher.membershipStatus ?? "Inactive";

  const selectedApp = useMemo(
    () => apps.find((app) => app.id === selectedApplicationId) ?? null,
    [apps, selectedApplicationId],
  );

  const daysLeft = useMemo(() => {
    if (!selectedApp) {
      return 0;
    }

    if (selectedApp.subscription_expires_at) {
      const expiresAt = new Date(selectedApp.subscription_expires_at).getTime();
      return Math.max(0, Math.ceil((expiresAt - nowTick) / (1000 * 60 * 60 * 24)));
    }

    return selectedApp.days_remaining;
  }, [nowTick, selectedApp]);

  const selectedDownloads = selectedApp?.downloads ?? 0;
  const selectedRating = selectedApp?.rating ?? 0;

  const appSlots = useMemo(
    () => {
      const slotCount = Math.max(BENCHMARK_SCROLL_SLOTS, apps.length);

      return Array.from({ length: slotCount }, (_, index) => ({
        id: apps[index]?.id ?? `placeholder-${index}`,
        isPlaceholder: index >= apps.length,
        index,
      }));
    },
    [apps],
  );

  const anyProviderAvailable = useMemo(() => {
    if (!renewProviders) {
      return false;
    }

    return renewProviders.stripe.available || renewProviders.razorpay.available;
  }, [renewProviders]);

  const handleRenew = async () => {
    if (!selectedApplicationId) {
      setMessage("Select an app to renew.");
      return;
    }

    setRenewLoading(true);
    setRenewQuote(null);
    setRenewProviders(null);
    setRenewTransactionId(null);
    setRenewStatus(null);
    setMessage("");

    try {
      const [quote, providerAvailability] = await Promise.all([
        fetchRenewalQuote(selectedApplicationId),
        fetchRenewalProviderAvailability(selectedApplicationId),
      ]);
      setRenewQuote(quote);
      setRenewProviders(providerAvailability.providers);

      if (
        !providerAvailability.providers.stripe.available &&
        !providerAvailability.providers.razorpay.available
      ) {
        setMessage("Online renewal payment is temporarily unavailable.");
      }
    } catch {
      setMessage("Unable to load renewal details for the selected app.");
    } finally {
      setRenewLoading(false);
    }
  };

  const handleStartRenewCheckout = async (provider: PaymentProvider) => {
    if (!selectedApplicationId || !renewQuote || !renewProviders) {
      return;
    }

    if (!renewProviders[provider].available) {
      setMessage(`${renewProviders[provider].displayName} is currently unavailable.`);
      return;
    }

    setRenewProviderLoading(provider);
    setMessage("");

    try {
      const transaction = await createRenewalIntent(selectedApplicationId, provider);
      setRenewTransactionId(transaction.paymentId);

      if (provider === "stripe") {
        if (transaction.checkoutUrl) {
          window.location.href = transaction.checkoutUrl;
          return;
        }

        setMessage("Stripe checkout is unavailable. Configure provider credentials.");
        return;
      }

      if (
        provider === "razorpay" &&
        transaction.providerOrderId &&
        transaction.razorpayKeyId
      ) {
        await openRazorpayRenewalCheckout({
          keyId: transaction.razorpayKeyId,
          orderId: transaction.providerOrderId,
          amountPaise: transaction.amountPaise,
          currency: transaction.currency,
          applicationName: renewQuote.applicationName,
          onDismiss: () => {
            setMessage("Renewal checkout was cancelled. Your app validity is unchanged.");
          },
          onSuccess: async (response) => {
            setMessage("Payment received. Verification in progress.");
            try {
              await verifyRazorpayRenewalPayment({
                paymentId: transaction.paymentId,
                orderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
            } catch {
              setMessage(
                "Payment received or pending. Verification may take a moment.",
              );
            }

            void refreshRenewalStatus(transaction.paymentId, true);
          },
        });
        return;
      }

      setMessage("Razorpay checkout is unavailable. Configure provider credentials.");
    } catch {
      setMessage(`Unable to start ${provider} renewal checkout.`);
    } finally {
      setRenewProviderLoading(null);
    }
  };

  const handleCancelRenew = () => {
    pollControllerRef.current += 1;
    setRenewQuote(null);
    setRenewProviders(null);
    setRenewTransactionId(null);
    setRenewStatus(null);
    setRenewStatusPolling(false);
    setMessage("");
  };

  const handleView = () => {
    if (!selectedApp) {
      setMessage("Select a published app to view.");
      return;
    }

    if (selectedApp.status !== "published") {
      setMessage("This app is not publicly available yet.");
      return;
    }

    navigate(`/apps/${selectedApp.slug}`, {
      state: { publisherDashboardContext: true },
    });
  };

  const handleEdit = () => {
    if (!selectedApplicationId) {
      setMessage("Select an app to edit.");
      return;
    }

    navigate(`/publish-your-app/${selectedApplicationId}/edit`);
  };

  const handleUpdate = () => {
    if (!selectedApplicationId) {
      setMessage("Select an app to update.");
      return;
    }

    navigate(`/publisher/apps/${selectedApplicationId}/update`);
  };

  const handleBin = () => {
    if (!selectedApp || !selectedApplicationId) {
      setMessage("Select an app to unpublish.");
      return;
    }

    if (selectedApp.status === "delisted") {
      setMessage("This app is already unpublished.");
      return;
    }

    if (!canOpenUnpublishConfirmation(selectedApp)) {
      setMessage("This app cannot be unpublished in its current state.");
      return;
    }

    setShowUnpublishConfirm(true);
    setMessage("");
  };

  const handleCancelUnpublish = () => {
    if (unpublishLoading) {
      return;
    }

    setShowUnpublishConfirm(false);
  };

  const handleConfirmUnpublish = async () => {
    if (!selectedApplicationId || !selectedApp || unpublishLoading) {
      return;
    }

    const requestToken = unpublishRequestRef.current + 1;
    unpublishRequestRef.current = requestToken;
    setUnpublishLoading(true);
    setMessage("");

    try {
      const result = await unpublishApplication(selectedApplicationId);

      if (unpublishRequestRef.current !== requestToken) {
        return;
      }

      setShowUnpublishConfirm(false);
      setMessage(resolveUnpublishSuccessMessage(result.alreadyUnpublished));
      await loadDashboard();
    } catch (error: unknown) {
      if (unpublishRequestRef.current !== requestToken) {
        return;
      }

      setMessage(resolveUnpublishErrorMessage(error));
    } finally {
      if (unpublishRequestRef.current === requestToken) {
        setUnpublishLoading(false);
      }
    }
  };

  const handleSelectApp = (app: PublisherDashboardApp | undefined) => {
    pollControllerRef.current += 1;
    unpublishRequestRef.current += 1;
    setMessage("");
    setShowUnpublishConfirm(false);
    setUnpublishLoading(false);
    setRenewQuote(null);
    setRenewProviders(null);
    setRenewTransactionId(null);
    setRenewStatus(null);
    setRenewStatusPolling(false);
    cachedSelectedApplicationId = app?.id ?? null;
    setSelectedApplicationId(app?.id ?? null);
  };

  const showRenewalFlow = Boolean(renewQuote || renewTransactionId || renewStatus);
  const binDisabled =
    unpublishLoading ||
    renewLoading ||
    renewProviderLoading !== null ||
    !canOpenUnpublishConfirmation(selectedApp);

  return (
    <section
      aria-labelledby="publisher-membership-title"
      className="oac-publisher-workspace"
    >
      <header className="oac-publisher-workspace__header">
        <h1
          className="oac-publisher-workspace__heading oac-membership-page__heading"
          id="publisher-membership-title"
        >
          Publisher Membership
        </h1>

        <p className="oac-publisher-workspace__welcome">Welcome back.</p>

        <p className="oac-publisher-workspace__intro">
          Manage your apps, profile, and membership—all in one place.
        </p>
      </header>

      <div className="oac-publisher-workspace__workspace">
        <div className="oac-publisher-workspace__toolbar-app-col">
          <div className="oac-publisher-workspace__publisher">
            <span className="oac-publisher-workspace__name">
              {publisherName}
            </span>

            <span className="oac-publisher-workspace__status">
              <span
                aria-hidden="true"
                className="oac-publisher-workspace__status-dot"
              />
              {membershipStatus}
            </span>
          </div>

          <button
            className="oac-btn oac-btn--blue oac-publisher-workspace__publish"
            onClick={() => navigate("/publish-your-app")}
            type="button"
          >
            Publish New App
          </button>
        </div>

        <div
          aria-hidden="true"
          className="oac-publisher-workspace__toolbar-col-spacer"
        />

        <button
          className="oac-btn oac-btn--outline oac-publisher-workspace__renew-top"
          disabled={renewLoading || renewProviderLoading !== null}
          onClick={() => void handleRenew()}
          type="button"
        >
          Renew
        </button>

        <div className="oac-publisher-workspace__workspace-main">
          <div className="oac-publisher-workspace__app-col">
            <div
              className="oac-publisher-workspace__app-grid-viewport app-scroll screen-08-app-scroll"
              style={
                { "--oac-publisher-grid-cols": GRID_COLUMNS } as CSSProperties
              }
            >
              <ul
                aria-label="Published apps"
                className="oac-publisher-workspace__app-grid"
              >
                {appSlots.map((slot) => {
                  const app = apps[slot.index];
                  const isSelected = app?.id === selectedApplicationId;

                  return (
                    <li key={slot.id}>
                      <button
                        aria-label={
                          slot.isPlaceholder
                            ? `App slot ${slot.index + 1}`
                            : app?.name || `App ${slot.index + 1}`
                        }
                        aria-pressed={isSelected}
                        className={[
                          "oac-publisher-workspace__app-slot",
                          isSelected
                            ? "oac-publisher-workspace__app-slot--selected"
                            : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => handleSelectApp(app)}
                        type="button"
                      >
                        <span
                          aria-hidden="true"
                          className="oac-publisher-workspace__app-slot-marker"
                        >
                          {isSelected && (
                            <svg fill="none" height="8" viewBox="0 0 10 8" width="10">
                              <path
                                d="M1 4.2L3.8 7L9 1.2"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                              />
                            </svg>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <nav
            aria-label="App management tools"
            className="oac-publisher-workspace__tools"
          >
            <button
              className="oac-publisher-workspace__tool"
              onClick={handleView}
              type="button"
            >
              <IconEye size={18} />
              <span>View</span>
            </button>

            <button
              className="oac-publisher-workspace__tool"
              onClick={handleEdit}
              type="button"
            >
              <IconPencil size={18} />
              <span>Edit</span>
            </button>

            <button
              className="oac-publisher-workspace__tool"
              onClick={handleUpdate}
              type="button"
            >
              <IconRefresh size={18} />
              <span>Update</span>
            </button>

            <button
              aria-disabled={binDisabled}
              className="oac-publisher-workspace__tool"
              disabled={binDisabled}
              onClick={handleBin}
              type="button"
            >
              <IconTrash size={18} />
              <span>Bin</span>
            </button>
          </nav>

          <aside
            aria-label="Membership summary"
            className="oac-publisher-workspace__summary"
          >
            {selectedApp && (
              <p className="oac-publisher-workspace__app-status">
                <span
                  aria-hidden="true"
                  className="oac-publisher-workspace__status-dot"
                />
                <span>{formatApplicationStatusLabel(selectedApp.status)}</span>
              </p>
            )}

            <dl className="oac-publisher-workspace__metrics">
              <div className="oac-publisher-workspace__metric">
                <dd className="oac-publisher-workspace__metric-value">
                  {metricsReady ? totalApps : METRIC_LOADING_PLACEHOLDER}
                </dd>
                <dt className="oac-publisher-workspace__metric-label">
                  Total Apps
                </dt>
              </div>

              <div className="oac-publisher-workspace__metric">
                <dd className="oac-publisher-workspace__metric-value">
                  {metricsReady ? daysLeft : METRIC_LOADING_PLACEHOLDER}
                </dd>
                <dt className="oac-publisher-workspace__metric-label">
                  Days left
                </dt>
              </div>

              <div className="oac-publisher-workspace__metric">
                <dd className="oac-publisher-workspace__metric-value">
                  {metricsReady
                    ? formatDownloads(selectedDownloads)
                    : METRIC_LOADING_PLACEHOLDER}
                </dd>
                <dt className="oac-publisher-workspace__metric-label">
                  {SELECTED_APP_DOWNLOADS_LABEL}
                </dt>
              </div>

              <div className="oac-publisher-workspace__metric">
                <dd className="oac-publisher-workspace__metric-value oac-publisher-workspace__metric-value--rating">
                  {metricsReady ? (
                    <StarRating
                      className="oac-publisher-workspace__stars"
                      rating={selectedRating}
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="oac-publisher-workspace__stars"
                    >
                      {METRIC_LOADING_PLACEHOLDER}
                    </span>
                  )}
                </dd>
                <dt className="oac-publisher-workspace__metric-label">
                  Overall Ratings
                </dt>
              </div>
            </dl>
          </aside>
        </div>
      </div>

      {renewLoading && !renewQuote && (
        <p className="oac-form-message">Loading renewal details…</p>
      )}

      {renewQuote && renewProviders && !renewTransactionId && !renewStatus && (
        <div className="oac-form-message">
          <p>
            Renew {renewQuote.applicationName}. Annual renewal:{" "}
            {formatInrPaise(renewQuote.nextRenewalPricePaise)}. Validity: +1 year.
          </p>
          <div>
            <button
              aria-disabled={!renewProviders.stripe.available}
              className="oac-btn oac-btn--blue"
              disabled={
                !renewProviders.stripe.available || renewProviderLoading !== null
              }
              onClick={() => void handleStartRenewCheckout("stripe")}
              type="button"
            >
              {renewProviders.stripe.available
                ? "Stripe"
                : "Stripe (unavailable)"}
            </button>
            <button
              aria-disabled={!renewProviders.razorpay.available}
              className="oac-btn oac-btn--outline"
              disabled={
                !renewProviders.razorpay.available ||
                renewProviderLoading !== null
              }
              onClick={() => void handleStartRenewCheckout("razorpay")}
              type="button"
            >
              {renewProviders.razorpay.available
                ? "Razorpay"
                : "Razorpay (unavailable)"}
            </button>
            <button
              className="oac-btn oac-btn--outline"
              disabled={renewProviderLoading !== null}
              onClick={handleCancelRenew}
              type="button"
            >
              Cancel
            </button>
          </div>
          {!anyProviderAvailable && (
            <p>Online renewal payment is temporarily unavailable.</p>
          )}
        </div>
      )}

      {(renewStatus || (renewTransactionId && !renewQuote)) && (
        <RenewalPaymentStatusPanel
          onBack={handleCancelRenew}
          onRefresh={
            renewTransactionId
              ? () => void refreshRenewalStatus(renewTransactionId, false)
              : undefined
          }
          polling={renewStatusPolling}
          quote={renewQuote}
          status={renewStatus}
        />
      )}

      {message && !showRenewalFlow && <p className="oac-form-message">{message}</p>}
      {message && showRenewalFlow && renewStatus && (
        <p className="oac-form-message">{message}</p>
      )}

      {showUnpublishConfirm && selectedApp && (
        <UnpublishConfirmPanel
          appName={selectedApp.name}
          loading={unpublishLoading}
          onCancel={handleCancelUnpublish}
          onConfirm={() => void handleConfirmUnpublish()}
        />
      )}
    </section>
  );
}
